// VIP Evaluator Gate
// Implements Work in Play Framework §4 (The Evaluator) + Appendix B
// (Evaluator Pause Modes). See vip-gate-component-spec.md for the full spec.
//
// AUTHORING CONTRACT (the HTML a case author writes, pasted as a Ghost
// HTML card - see the spec §4 for full worked examples):
//
//   <div class="pause-decision" data-case="CSE-004" data-pause="p2">
//     <div class="gate-fork">
//       <p class="gate-eyebrow">A decision</p>
//       <p>...setup prose...</p>
//       <p class="gate-question">What does she do?</p>
//     </div>
//     <div class="gate-choices">
//       <label><input type="radio" name="cse-004-p2" value="a"> Option A</label>
//       <label><input type="radio" name="cse-004-p2" value="b"> Option B</label>
//     </div>
//     <div class="gate-reason">
//       <label for="cse-004-p2-reason">In one line: your reason.</label>
//       <textarea id="cse-004-p2-reason" rows="2"></textarea>
//     </div>
//     <button class="gate-commit" type="button">Commit</button>
//     <div class="gate-rubric">
//       <p class="gate-eyebrow">What's actually on the table</p>
//       <p>...two-sided rubric prose...</p>
//       <p class="gate-selfcheck">Did your reason touch both sides - or only one?</p>
//       <button class="gate-unlock" type="button">See what she did</button>
//     </div>
//     <div class="gate-reveal">
//       <p>...decoupled-consequence prose...</p>
//       <div class="gate-takeaway">
//         <p><strong>You said:</strong> <span data-echo="choice"></span>
//            - because <span data-echo="reason"></span></p>
//       </div>
//     </div>
//   </div>
//
// Recognition mode (.pause-recognition) is the same shape minus
// .gate-choices and .gate-rubric - reason/naming leads straight to
// .gate-reveal on commit, no rubric step.
//
// Rules the markup must follow (a malformed block is left fully
// visible and un-gated - see assertGateContract below):
//   - data-case + data-pause together form the storage key; data-pause
//     must be unique within a case.
//   - Decision: .gate-choices needs >=2 radios sharing one `name`, plus
//     a .gate-rubric containing a .gate-unlock button.
//   - Recognition: no .gate-choices, no .gate-rubric.
//   - Both modes: exactly one .gate-reason textarea, one .gate-commit
//     button, one .gate-reveal.
//   - .gate-takeaway echoes use data-echo="choice" / data-echo="reason".
//
// This component never grades or scores a choice (Framework invariant
// 2) - it only gates the reveal behind a commitment and hands the
// reader's own reasoning back to them. `sawBoth` in the stored
// commitment stays null at MVP: the self-check line is rhetorical
// prose, not a captured response - no control exists yet to set it.

(function() {
    'use strict';

    var STORAGE_PREFIX = 'vip:commit:';
    var memoryStore = Object.create(null);

    function storageKey(caseId, pauseId) {
        return STORAGE_PREFIX + caseId + ':' + pauseId;
    }

    // Storage abstraction (spec §6) - localStorage today, swappable for
    // a backend later with zero changes to the component's call sites.
    // Never throws into the UI: a storage failure (private mode, quota)
    // silently degrades to an in-memory, session-only store.
    var VIPGateStore = {
        get: function(caseId, pauseId) {
            var key = storageKey(caseId, pauseId);
            return Promise.resolve().then(function() {
                try {
                    var raw = window.localStorage.getItem(key);
                    if (raw) { return JSON.parse(raw); }
                } catch (e) {
                    // storage unavailable - fall through to the memory store
                }
                return memoryStore[key] || null;
            });
        },
        save: function(commitment) {
            var key = storageKey(commitment.caseId, commitment.pauseId);
            memoryStore[key] = commitment;
            return Promise.resolve().then(function() {
                try {
                    window.localStorage.setItem(key, JSON.stringify(commitment));
                } catch (e) {
                    // degrade to memory-only for this session
                }
                return commitment;
            });
        }
    };

    // Exposed for a future backend swap and for debugging - not required
    // by any call site inside this file.
    window.VIPGateStore = VIPGateStore;

    function emit(name, detail) {
        document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    }

    // Reader's Ghost member uuid (pseudonymous), rendered server-side onto
    // <body data-member-id> only when signed in (see default.hbs). Empty
    // for anonymous readers.
    function currentMemberId() {
        try {
            return document.body.getAttribute('data-member-id') || '';
        } catch (e) {
            return '';
        }
    }

    // GDPR disclosure (spec §10): since commit data now leaves the device
    // (vipCaptureCommit below), every gate must say so before the reader
    // commits. Inserted once per block, right after .gate-reason, so it's
    // visible in the PROMPT state - not dependent on case authors
    // remembering to paste privacy copy themselves.
    function ensurePrivacyNote(block, mode) {
        if (block.querySelector('.gate-privacy-note')) { return; }
        var reasonBlock = block.querySelector('.gate-reason');
        if (!reasonBlock) { return; }
        var note = document.createElement('p');
        note.className = 'gate-privacy-note';
        var whatIsRecorded = mode === 'decision' ? 'your choice and reason' : 'what you name here';
        note.textContent = 'On commit, ' + whatIsRecorded + ' (and, if you’re signed in, a pseudonymous member ID) ' +
            'are recorded to study how readers reason through these decisions. No account is required, and this stays ' +
            'in the site owner’s own records — please don’t include personal details in your reason.';
        reasonBlock.parentNode.insertBefore(note, reasonBlock.nextSibling);
    }

    // Fire-and-forget capture of a gate commit to the owner's Google Sheet
    // (via an Apps Script Web App). Never awaited by a call site, never
    // blocks or delays the reveal, and fails silently if the endpoint is
    // unreachable - the reveal never depends on this succeeding.
    function vipCaptureCommit(p) {
        try {
            fetch('https://script.google.com/macros/s/AKfycbxfIJoTyVhaa1_rS4yLIuAH-p7mMhWyobj6VT5PAHY3k8UhPNTxp19oYh3QTM8kkGYy/exec', {
                method: 'POST',
                mode: 'no-cors',
                headers: { 'Content-Type': 'text/plain;charset=utf-8' },
                keepalive: true,
                body: JSON.stringify({
                    token: 'VIPCommits20261234321',
                    case: p.case, pause: p.pause, choice: p.choice, reason: p.reason,
                    member_id: p.member_id || ''
                })
            }).catch(function () {});
        } catch (e) {}
    }

    function labelTextFor(radio) {
        if (radio.id) {
            var byFor = document.querySelector('label[for="' + radio.id.replace(/"/g, '\\"') + '"]');
            if (byFor) { return byFor.textContent.trim(); }
        }
        var wrapping = radio.closest('label');
        return wrapping ? wrapping.textContent.trim() : radio.value;
    }

    function fillEchoes(container, commitment) {
        var choiceEl = container.querySelector('[data-echo="choice"]');
        var reasonEl = container.querySelector('[data-echo="reason"]');
        if (choiceEl) { choiceEl.textContent = commitment.choiceLabel || ''; }
        if (reasonEl) { reasonEl.textContent = commitment.reason || ''; }
    }

    function revealRegion(el) {
        el.hidden = false;
        el.classList.add('is-gate-appearing');
        // Double rAF: let the browser paint the pre-transition state
        // before removing the class triggers the CSS transition.
        // Reduced-motion is handled purely in CSS (transition: none),
        // so no branching is needed here.
        requestAnimationFrame(function() {
            requestAnimationFrame(function() {
                el.classList.remove('is-gate-appearing');
            });
        });
    }

    function focusRegionHeading(regionEl) {
        // Prefer the region's own eyebrow as the focus target (a short,
        // contained announcement). Not every region has one (the plain
        // .gate-reveal example in the spec doesn't) - fall back to its
        // first element child rather than the whole region, so a screen
        // reader announces one opening line, not the entire block's text.
        var heading = regionEl.querySelector('.gate-eyebrow') || regionEl.firstElementChild || regionEl;
        if (!heading.hasAttribute('tabindex')) { heading.setAttribute('tabindex', '-1'); }
        heading.focus();
    }

    function createLiveRegion(block) {
        var region = document.createElement('div');
        region.className = 'vip-gate-sr-only';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        block.appendChild(region);
        return region;
    }

    function announce(region, text) {
        region.textContent = text;
    }

    // Checks the authored markup against the contract in §4c. Returns
    // null if valid, or a string reason if not - callers must fail open
    // (leave the block fully visible, un-gated) on any non-null result.
    function assertGateContract(block, isDecision) {
        if (!block.getAttribute('data-case') || !block.getAttribute('data-pause')) {
            return 'missing data-case/data-pause';
        }
        if (!block.querySelector('.gate-reason textarea')) {
            return 'missing .gate-reason textarea';
        }
        if (!block.querySelector('.gate-commit')) {
            return 'missing .gate-commit button';
        }
        if (!block.querySelector('.gate-reveal')) {
            return 'missing .gate-reveal';
        }

        var choicesWrap = block.querySelector('.gate-choices');
        var rubric = block.querySelector('.gate-rubric');

        if (isDecision) {
            var radios = choicesWrap ? choicesWrap.querySelectorAll('input[type="radio"]') : [];
            if (!choicesWrap || radios.length < 2) {
                return 'decision mode needs .gate-choices with at least 2 radio inputs';
            }
            if (!rubric || !rubric.querySelector('.gate-unlock')) {
                return 'decision mode needs .gate-rubric containing a .gate-unlock button';
            }
        } else {
            if (choicesWrap) { return 'recognition mode must not have .gate-choices'; }
            if (rubric) { return 'recognition mode must not have .gate-rubric'; }
        }

        return null;
    }

    function initGate(block) {
        var isDecision = block.classList.contains('pause-decision');
        var isRecognition = block.classList.contains('pause-recognition');
        if (!isDecision && !isRecognition) { return; }

        var problem = assertGateContract(block, isDecision);
        if (problem) {
            console.warn('[vip-gate] malformed gate block, leaving un-gated (' + problem + '):', block);
            return;
        }

        var caseId = block.getAttribute('data-case');
        var pauseId = block.getAttribute('data-pause');
        var mode = isDecision ? 'decision' : 'recognition';

        ensurePrivacyNote(block, mode);

        var fork = block.querySelector('.gate-fork');
        var questionEl = fork ? fork.querySelector('.gate-question') : null;
        var choicesWrap = block.querySelector('.gate-choices');
        var radios = choicesWrap ? Array.prototype.slice.call(choicesWrap.querySelectorAll('input[type="radio"]')) : [];
        var reasonInput = block.querySelector('.gate-reason textarea');
        var commitBtn = block.querySelector('.gate-commit');
        var rubric = block.querySelector('.gate-rubric');
        var unlockBtn = rubric ? rubric.querySelector('.gate-unlock') : null;
        var reveal = block.querySelector('.gate-reveal');
        var liveRegion = createLiveRegion(block);

        if (questionEl && choicesWrap) {
            if (!questionEl.id) { questionEl.id = 'gate-question-' + caseId + '-' + pauseId; }
            choicesWrap.setAttribute('role', 'radiogroup');
            choicesWrap.setAttribute('aria-labelledby', questionEl.id);
        }

        function lockPrompt() {
            radios.forEach(function(radio) { radio.disabled = true; });
            reasonInput.disabled = true;
            reasonInput.setAttribute('readonly', 'readonly');
            commitBtn.hidden = true;
            block.classList.add('is-gate-committed');
        }

        function renderCommitted(commitment) {
            if (isDecision) {
                radios.forEach(function(radio) {
                    radio.checked = (radio.value === commitment.choice);
                });
            }
            reasonInput.value = commitment.reason;
            lockPrompt();
            if (unlockBtn) { unlockBtn.hidden = true; }
            if (rubric) { rubric.hidden = false; }
            fillEchoes(reveal, commitment);
            reveal.hidden = false;
        }

        function wireInteractive() {
            if (rubric) { rubric.hidden = true; }
            reveal.hidden = true;

            function updateCommitEnabled() {
                var reasonOk = reasonInput.value.trim().length >= 3;
                var choiceOk = !isDecision || radios.some(function(radio) { return radio.checked; });
                commitBtn.disabled = !(reasonOk && choiceOk);
            }

            reasonInput.addEventListener('input', updateCommitEnabled);
            radios.forEach(function(radio) {
                radio.addEventListener('change', updateCommitEnabled);
            });
            updateCommitEnabled();

            var committed = false;

            commitBtn.addEventListener('click', function() {
                if (committed) { return; }
                committed = true;

                var chosenRadio = isDecision ? radios.filter(function(radio) { return radio.checked; })[0] : null;
                var commitment = {
                    schemaVersion: 1,
                    caseId: caseId,
                    pauseId: pauseId,
                    mode: mode,
                    choice: chosenRadio ? chosenRadio.value : null,
                    choiceLabel: chosenRadio ? labelTextFor(chosenRadio) : null,
                    reason: reasonInput.value.trim(),
                    sawBoth: null,
                    committedAt: new Date().toISOString()
                };

                VIPGateStore.save(commitment).then(function(saved) {
                    lockPrompt();
                    emit('vip:gate:commit', { caseId: caseId, pauseId: pauseId, mode: mode });
                    vipCaptureCommit({
                        case: caseId,
                        pause: pauseId,
                        choice: commitment.choice,
                        reason: commitment.reason,
                        member_id: currentMemberId()
                    });

                    if (isDecision) {
                        announce(liveRegion, 'Your response is saved.');
                        // Let the amber recede be witnessed before the rubric
                        // arrives (design brief §3b) - skipped under reduced
                        // motion, where the recede itself is instant too.
                        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                        setTimeout(function() {
                            revealRegion(rubric);
                            focusRegionHeading(rubric);
                        }, reduceMotion ? 0 : 250);
                    } else {
                        fillEchoes(reveal, saved);
                        revealRegion(reveal);
                        focusRegionHeading(reveal);
                        announce(liveRegion, 'Your response is saved. Here\'s what happened.');
                        emit('vip:gate:reveal', { caseId: caseId, pauseId: pauseId, mode: mode });
                    }
                });
            });

            if (unlockBtn) {
                unlockBtn.addEventListener('click', function() {
                    VIPGateStore.get(caseId, pauseId).then(function(saved) {
                        fillEchoes(reveal, saved);
                        revealRegion(reveal);
                        focusRegionHeading(reveal);
                        announce(liveRegion, 'Here\'s what happened.');
                        emit('vip:gate:reveal', { caseId: caseId, pauseId: pauseId, mode: mode });
                    });
                });
            }
        }

        VIPGateStore.get(caseId, pauseId).then(function(existing) {
            if (existing) {
                renderCommitted(existing);
            } else {
                wireInteractive();
            }
        });
    }

    function init() {
        try {
            var blocks = document.querySelectorAll('.pause-decision, .pause-recognition');
            Array.prototype.forEach.call(blocks, function(block) {
                try {
                    initGate(block);
                } catch (e) {
                    console.warn('[vip-gate] failed to initialize, leaving as authored:', e, block);
                }
            });
        } catch (e) {
            // Anything above the per-block loop (e.g. querySelectorAll itself)
            // fails open per §9: every gate block is left exactly as authored,
            // full visible prose, rather than half-gated.
            console.warn('[vip-gate] top-level init failed, gates left as authored:', e);
        }
    }

    // Bootstrapping is wrapped too (spec §14.3): all theme JS concatenates
    // into one casper.js, so this guards against this component's own setup
    // throwing and taking down anything concatenated after it in the bundle.
    try {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
        } else {
            init();
        }
    } catch (e) {
        console.warn('[vip-gate] failed to bootstrap:', e);
    }
})();
